import { atom } from "recoil";
import { recoilPersist } from "recoil-persist";

// Create separate persistence instances for different security levels
const { persistAtom: persistToSession } = recoilPersist({
  key: "recoil-session",
  storage: sessionStorage, // More secure - cleared when tab closes
});

const { persistAtom: persistToLocal } = recoilPersist({
  key: "recoil-persist",
  storage: localStorage, // For less sensitive user preferences
});

export const platformState = atom({
  key: "platform",
  default: {},
  effects_UNSTABLE: [persistToLocal],
});

// Security-sensitive tokens - stored in sessionStorage (cleared on tab close)
export const accessTokenState = atom({
  key: "accessToken",
  default: "",
  effects_UNSTABLE: [persistToSession],
});

export const developerAccessTokenState = atom({
  key: "developerAccessToken",
  default: "",
  effects_UNSTABLE: [persistToSession],
});

// User preferences and non-sensitive data - localStorage is fine
export const userInfoState = atom({
  key: "userInfo",
  default: {},
  effects_UNSTABLE: [persistToLocal],
});

export const activeScopeState = atom({
  key: "activeScope",
  default: null,
  effects_UNSTABLE: [persistToLocal],
});

export const scopesState = atom({
  key: "scopes",
  default: [],
  effects_UNSTABLE: [persistToLocal],
});

export const promptOverridesState = atom({
  key: "promptOverridesState",
  default: {},
  effects_UNSTABLE: [persistToLocal],
});

export const selectedPromptIdState = atom({
  key: "selectedPromptId",
  default: null,
  effects_UNSTABLE: [persistToLocal],
});

// UI state - persisted to localStorage for user experience
export const currentAutomationState = atom({
  key: "currentAutomationState",
  default: { automationId: null, scriptId: null, name: null },
  effects_UNSTABLE: [persistToLocal],
});

export const unreadChatMessagesState = atom({
  key: "unreadChatMessages",
  default: 0,
  effects_UNSTABLE: [persistToLocal],
});

export const isDefaultBackgroundState = atom({
  key: "isDefaultBackground",
  default: true,
  effects_UNSTABLE: [persistToLocal],
});

export const backgroundTypeState = atom({
  key: "backgroundType",
  default: "default",
  effects_UNSTABLE: [persistToLocal],
});

export const backgroundOpacityState = atom({
  key: "backgroundOpacity",
  default: "medium",
  effects_UNSTABLE: [persistToLocal],
});

export const primaryColorState = atom({
  key: "primaryColor",
  default: "default",
  effects_UNSTABLE: [persistToLocal],
});

// General state for any type of mention from anywhere in the app
export const selectedMentionState = atom({
  key: "selectedMentionState",
  default: null,
  effects_UNSTABLE: [persistToLocal],
});

// Input mode preference - persisted to localStorage for user experience
export const inputModeState = atom({
  key: "inputMode",
  default: "text", // "text" or "voice"
  effects_UNSTABLE: [persistToLocal],
});

// Safe area insets from native platform (no persistence needed - fetched on each app start)
export const safeAreaInsetsState = atom({
  key: "safeAreaInsets",
  default: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
});

// Scope preference data - persisted to localStorage for user experience
export const scopePreferenceState = atom({
  key: "scopePreference",
  default: {},
  effects_UNSTABLE: [persistToLocal],
});

// Current canvas ID - persisted to localStorage for quick dashboard loading
export const currentCanvasIdState = atom({
  key: "currentCanvasId",
  default: null,
  effects_UNSTABLE: [persistToLocal],
});

// WebSocket ready state - not persisted, always starts from false on app start
export const wsReadyState = atom({
  key: "wsReady",
  default: false,
});

// Cloud ready state - not persisted, always starts from false on app start
export const cloudReadyState = atom({
  key: "cloudReady",
  default: false,
});

// Dialogs state - persisted to localStorage for dialog management
export const dialogsState = atom({
  key: "dialogs",
  default: [],
  effects_UNSTABLE: [persistToLocal],
});

// Automation active tab - persisted to localStorage for user experience
export const automationActiveTabState = atom({
  key: "automationActiveTab",
  default: 0, // 0: Recurring, 1: Task Flow, 2: History
  effects_UNSTABLE: [persistToLocal],
});

// Chat sidebar visibility - persisted to localStorage for user experience
export const chatSidebarVisibleState = atom({
  key: "chatSidebarVisible",
  default: true,
  effects_UNSTABLE: [persistToLocal],
});

// Developer email - persisted to localStorage for developer authentication
export const developerEmailState = atom({
  key: "developerEmail",
  default: "",
  effects_UNSTABLE: [persistToLocal],
});
